import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Sparkles, Mail, Lock, User, ArrowLeft, X, Check } from 'lucide-react';
import axios from 'axios';
import { track, EVENTS } from '../utils/analytics';
import { trackSignup, trackStartTrial, trackInitiateCheckout } from '../utils/tiktokPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = () => {
  const { login, register, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan');
  const inviteCode = searchParams.get('invite');
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const modeParam = (searchParams.get('mode') || '').toLowerCase();
  const nextParam = (searchParams.get('next') || '').toLowerCase();
  const funnelParam = (searchParams.get('funnel') || '').toLowerCase(); // 'credit' = freemium A/B variant
  // ?mode=signup forces the signup tab. ?next=trial means: as soon as the user
  // creates an account, kick off the Stripe trial checkout without showing the
  // pricing page in between (Petrus, 2026-05-19: reduce drop-off after CTA).
  const trialMode = nextParam === 'trial' || modeParam === 'trial';

  const [isLogin, setIsLogin] = useState(
    !planFromUrl && !inviteCode && modeParam !== 'signup' && !trialMode
  );
  const [isForgot, setIsForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  // Email-only first step for trial signup (only when trialMode + !isLogin)
  // Step 1 = email; Step 2 = name + password.
  const [signupStep, setSignupStep] = useState(1);
  const promoCode = (searchParams.get('promo') || '').toUpperCase();

  // Capture the email as an abandoned-form lead the moment the user moves
  // from Step 1 → Step 2. Fire-and-forget; never block the UI.
  const captureLead = (email, plan = 'basic') => {
    try {
      const e = (email || '').trim().toLowerCase();
      if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
      axios.post(`${API}/analytics/capture-lead`, {
        email: e,
        source: 'signup_step1',
        plan_id: plan,
        promo_code: promoCode || undefined,
      }).catch(() => {});
    } catch (_) {}
  };

  const handleStep1Continue = (e) => {
    e?.preventDefault?.();
    const email = formData.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    captureLead(email);
    setSignupStep(2);
  };

  const startCheckout = async (planId, token) => {
    try {
      // Free plan - activate directly, no Stripe
      if (planId === 'free') {
        await axios.post(`${API}/subscription/activate-free`, {}, { headers: { Authorization: `Bearer ${token}` } });
        navigate('/dashboard');
        return;
      }
      const isTrialStart = false; // v1: card-free trial granted at signup
      const endpoint = isTrialStart ? `${API}/subscription/start-trial` : `${API}/subscription/checkout`;
      // TikTok Pixel — fire just before we redirect away to Stripe
      try {
        trackInitiateCheckout({ plan: planId, currency: 'EUR', value: isTrialStart ? 0 : 0 });
      } catch (e) {}
      const response = await axios.post(
        endpoint,
        { plan_id: planId, origin_url: window.location.origin, promo_code: promoCode || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
    } catch (error) {
      console.error('Checkout redirect failed:', error);
    }
    navigate('/subscription?setup=true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (!isLogin && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        setLoading(false);
        return;
      }
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        track(EVENTS.LOGIN);
        toast.success('Welcome back!');
        if (planFromUrl) {
          const token = localStorage.getItem('token');
          await startCheckout(planFromUrl, token);
        } else if (funnelParam === 'credit' || userData.signup_funnel === 'credit') {
          // Credit-funnel users (returning or on the /credit CTA) NEVER go to Stripe.
          // Skip the paywall check entirely and drop them into the app.
          navigate('/swipe');
        } else if (!userData.is_founder && (userData.subscription_plan === 'expired_trial' || userData.subscription_plan === 'unpaid')) {
          navigate('/subscription?setup=true');
        } else {
          navigate(redirectTo);
        }
      } else {
        track(EVENTS.SIGNUP_STARTED, { funnel: funnelParam || 'subscription' });
        const result = await register(formData.name, formData.email, formData.password, inviteCode || undefined, funnelParam || undefined);
        track(EVENTS.SIGNUP_COMPLETED, { funnel: funnelParam || 'subscription' });
        // TikTok Pixel — signup conversion
        try {
          trackSignup({ method: 'email', email: formData.email, userId: result?.id });
          if (!inviteCode && funnelParam !== 'credit') trackStartTrial({ value: 0, currency: 'EUR', email: formData.email, userId: result?.id });
        } catch (e) {}
        toast.success('Account created! Let\'s get you started.');
        const token = localStorage.getItem('token');
        // Whitelisted invites bypass paid trial
        if (inviteCode === 'free' || inviteCode === 'beta2026') {
          navigate('/dashboard');
        } else if (funnelParam === 'credit') {
          // CREDIT FUNNEL (A/B variant): land them DIRECTLY in the app.
          // The FirstRunCVModal popup will auto-fire because they have no CV
          // yet, prompting an in-context upload — no separate onboarding page.
          navigate('/swipe?firstrun=1');
        } else {
          // v1: every new account is on a 7-day card-free trial → upload CV → swipe feed.
          navigate('/onboarding/cv');
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || (error.request ? 'Unable to reach the server. Please try again.' : 'Something went wrong');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-[#0a0a0a] flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-500 dark:text-zinc-500 hover:text-indigo-600 dark:hover:text-indigo-400 mb-6 transition-colors text-sm"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>

          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-zinc-200 dark:border-white/[0.08] shadow-lg dark:shadow-2xl p-8">
            <div className="flex items-center mb-6">
              <img src="https://static.prod-images.emergentagent.com/jobs/4621c343-adba-42bf-84be-49e31a6afafb/images/40c8a552cca65f4dba8d2de4cceb45ed6fa5e8f8b68ecd07be2fab8cf5594a0e.png" alt="JobSurf AI" className="h-16 w-16 object-contain rounded-xl dark:brightness-110 dark:contrast-110" />
            </div>

            <h1 className="font-heading text-2xl font-bold text-zinc-900 dark:text-white mb-1">
              {isForgot 
                ? (forgotSent ? 'Check your email' : 'Reset your password')
                : isLogin ? 'Welcome back' 
                : 'Start your 7-day free trial'}
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-6">
              {isForgot 
                ? (forgotSent 
                  ? 'We sent a password reset link to your email. It expires in 1 hour.'
                  : 'Enter your email and we\'ll send you a reset link')
                : isLogin 
                  ? 'Sign in to continue your job search' 
                                    : inviteCode === 'beta2026' ? 'Beta Tester Access — Full Unlimited plan, completely free'
                  : inviteCode === 'free' ? 'Sign up to get free access — no credit card needed'
                  : '7 days free, 25 AI tailorings included. No card required.'}
            </p>

          {isForgot ? (
            forgotSent ? (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl text-center">
                  <Mail className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">Reset link sent to <strong>{formData.email}</strong></p>
                </div>
                <Button
                  onClick={() => { setIsForgot(false); setForgotSent(false); }}
                  className="w-full h-12 bg-zinc-100 dark:bg-white/[0.06] hover:bg-zinc-200 dark:hover:bg-white/[0.1] text-zinc-900 dark:text-white rounded-xl font-medium"
                  data-testid="back-to-login-btn"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-700 dark:text-zinc-300 font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-12 pl-12 rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-[#111] dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-medium transition-all shadow-lg shadow-indigo-500/25"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : 'Send Reset Link'}
                </Button>
                <button
                  type="button"
                  onClick={() => setIsForgot(false)}
                  className="w-full text-center text-zinc-600 dark:text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors text-sm"
                  data-testid="back-to-login-link"
                >
                  Back to Sign In
                </button>
              </form>
            )
          ) : (
          <>
          {/* Google Sign-In temporarily disabled (2026-05-19) — switching to direct
              Google OAuth under jobsurfai.com (no more auth.emergentagent.com middle page).
              Re-enable by uncommenting the block below once GOOGLE_CLIENT_ID / SECRET are wired. */}
          {false && (
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              disabled={loading}
              data-testid="google-signin-btn"
              className="w-full h-12 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-[#0f0f0f] text-zinc-800 dark:text-zinc-100 font-medium flex items-center justify-center gap-3 hover:bg-zinc-50 dark:hover:bg-zinc-900 active:scale-[0.99] transition-all shadow-sm mb-4"
            >
              Continue with Google
            </button>
          )}

          {/* Step 1 of trial signup — email only (lower-friction conversion) */}
          {trialMode && !isLogin && signupStep === 1 ? (
          <form onSubmit={handleStep1Continue} className="space-y-5" data-testid="trial-step-1-form">
            <div className="space-y-2">
              <Label htmlFor="email-step1" className="text-zinc-700 dark:text-zinc-300 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="email-step1"
                  name="email"
                  type="email"
                  autoFocus
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12 pl-12 rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-[#111] dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20"
                  required
                  data-testid="trial-step1-email"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-medium transition-all shadow-lg shadow-indigo-500/25"
              data-testid="trial-step1-continue-btn"
            >
              Continue →
            </Button>
            <p className="text-[11px] text-center text-zinc-500">
              By continuing, you agree to our <Link to="/terms" className="text-indigo-400 hover:underline">Terms</Link> &middot; <Link to="/privacy" className="text-indigo-400 hover:underline">Privacy</Link>
            </p>
          </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-zinc-700 dark:text-zinc-300 font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    className="h-12 pl-12 rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-[#111] dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20"
                    required={!isLogin}
                    data-testid="auth-name-input"
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-700 dark:text-zinc-300 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12 pl-12 rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-[#111] dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20"
                  required
                  data-testid="auth-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-700 dark:text-zinc-300 font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-12 pl-12 rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-[#111] dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20"
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
              </div>
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-zinc-700 dark:text-zinc-300 font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`h-12 pl-12 rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-[#111] dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''}`}
                    required
                    minLength={6}
                    data-testid="auth-confirm-password-input"
                  />
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1" data-testid="password-mismatch-error">Passwords do not match</p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-medium transition-all shadow-lg shadow-indigo-500/25"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                isLogin ? 'Sign In' : 'Start Free'
              )}
            </Button>

            {isLogin && (
              <button
                type="button"
                onClick={() => setIsForgot(true)}
                className="w-full text-center text-sm text-zinc-500 dark:text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            )}
          </form>
          )}
          </>
          )}

          {!isForgot && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-zinc-600 dark:text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? (
                <>Don't have an account? <span className="font-medium text-indigo-600">Sign up</span></>
              ) : (
                <>Already have an account? <span className="font-medium text-indigo-600">Sign in</span></>
              )}
            </button>
          </div>
          )}
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Swipe Demo */}
      <div className="hidden lg:flex flex-1 bg-[#0f0f0f] items-center justify-center p-12 relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/10 via-transparent to-purple-600/10" />
        
        <div className="relative z-10 w-full max-w-sm">
          <h2 className="font-heading text-2xl font-bold text-white text-center mb-2">Swipe. Match. Tailor. Apply.</h2>
          <p className="text-zinc-500 text-sm text-center mb-8">Right if you're interested, left to skip — then tailor your CV and apply on the employer's site</p>

          {/* Mock swipe cards stack */}
          <div className="relative h-[380px]">
            {/* Background card 3 */}
            <motion.div
              className="absolute inset-x-6 top-6 h-[340px] bg-[#1a1a1a] rounded-2xl border border-white/[0.05]"
              animate={{ scale: 0.92 }}
            />
            {/* Background card 2 */}
            <motion.div
              className="absolute inset-x-3 top-3 h-[340px] bg-[#1e1e1e] rounded-2xl border border-white/[0.06]"
              animate={{ scale: 0.96 }}
            />
            {/* Front card - animated swipe */}
            <motion.div
              className="absolute inset-0 h-[340px] bg-[#222] rounded-2xl border border-white/[0.08] shadow-2xl p-6 flex flex-col"
              animate={{
                x: [0, 0, 0, 120, 120, 0, 0, 0, -120, -120, 0],
                rotate: [0, 0, 0, 8, 8, 0, 0, 0, -8, -8, 0],
                opacity: [1, 1, 1, 0.7, 0, 1, 1, 1, 0.7, 0, 1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                times: [0, 0.15, 0.2, 0.3, 0.35, 0.45, 0.6, 0.65, 0.75, 0.8, 1],
              }}
            >
              {/* Match badge */}
              <div className="flex items-center justify-between mb-4">
                <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-xs font-bold">87% Match</span>
                <span className="text-zinc-500 text-xs">2h ago</span>
              </div>

              {/* Job info */}
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg leading-snug">Senior Account Executive</h3>
                <p className="text-indigo-400 font-medium text-sm mt-1">Salesforce</p>
                <p className="text-zinc-500 text-sm mt-1">Barcelona, Spain</p>
                <p className="text-emerald-400 font-semibold text-sm mt-2">$95k - $130k</p>

                {/* Skills */}
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {['B2B SaaS', 'Enterprise Sales', 'CRM', 'Pipeline'].map(skill => (
                    <span key={skill} className="bg-indigo-500/15 text-indigo-300 px-2.5 py-1 rounded-md text-xs font-medium">{skill}</span>
                  ))}
                </div>

                <p className="text-zinc-500 text-xs mt-4 line-clamp-3 leading-relaxed">
                  Lead enterprise sales initiatives across EMEA. Drive revenue growth through strategic account management and new business development...
                </p>
              </div>

              {/* Swipe indicators */}
              <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                <div className="flex items-center gap-2 text-red-400/60">
                  <X className="w-5 h-5" />
                  <span className="text-xs font-medium">Skip</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-400/60">
                  <span className="text-xs font-medium">Apply</span>
                  <Check className="w-5 h-5" />
                </div>
              </div>
            </motion.div>

            {/* Swipe direction indicators */}
            <motion.div
              className="absolute -left-4 top-1/2 -translate-y-1/2 bg-red-500/20 text-red-400 rounded-full p-2"
              animate={{ opacity: [0, 0, 0, 0, 0, 0, 0, 0.8, 0.8, 0, 0] }}
              transition={{ duration: 8, repeat: Infinity, times: [0, 0.15, 0.2, 0.3, 0.35, 0.45, 0.6, 0.65, 0.75, 0.8, 1] }}
            >
              <X className="w-5 h-5" />
            </motion.div>
            <motion.div
              className="absolute -right-4 top-1/2 -translate-y-1/2 bg-emerald-500/20 text-emerald-400 rounded-full p-2"
              animate={{ opacity: [0, 0, 0, 0.8, 0.8, 0, 0, 0, 0, 0, 0] }}
              transition={{ duration: 8, repeat: Infinity, times: [0, 0.15, 0.2, 0.3, 0.35, 0.45, 0.6, 0.65, 0.75, 0.8, 1] }}
            >
              <Check className="w-5 h-5" />
            </motion.div>
          </div>

          {/* Bottom stats */}
          <div className="flex justify-center gap-6 mt-6">
            {[
              { label: 'Job sources', value: '20+' },
              { label: 'Free trial', value: '7 days' },
              { label: 'AI tailorings included', value: '25' },
            ].map(stat => (
              <div key={stat.label} className="text-center">
                <div className="text-white font-bold text-lg">{stat.value}</div>
                <div className="text-zinc-600 text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
